"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Lock, ShieldCheck, Sparkles, TriangleAlert } from "lucide-react";
import { InputOTP } from "@/components/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SocialAuthButton, type SocialAuthProvider } from "@/components/ui/social-auth-button";
import { toast } from "@/components/ui/use-toast";

import { Logo } from "@/components/shell/logo";
import { useOAuthProviders, useRequestOtp, useVerifyOtp, useVerifyTotpLogin } from "@/lib/auth/hooks";

const CHIPS = ["GPT", "Claude", "Gemini", "Groq", "Deepgram", "ElevenLabs", "Live Calls", "Telephony"];

// Placeholder wordmarks, swap for real customer logos once approved for use.
const TRUST = ["TensorGo", "Enterprise", "Contact Centres", "Global Banks"];

const BADGES = [
  { icon: ShieldCheck, label: "Enterprise Security" },
  { icon: Lock, label: "End-to-End Encryption" },
  { icon: Sparkles, label: "Magic Link Authentication" },
];

const OAUTH_START: Record<SocialAuthProvider, string> = {
  google: "/api/auth/oauth/google/start",
  microsoft: "/api/auth/oauth/microsoft/start",
};

/** Ambient floating particles, cheap transforms only, disabled for reduced motion. */
function Particles({ count = 18 }: { count?: number }) {
  const reduce = useReducedMotion();
  const dots = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: (i * 37) % 100,
        top: (i * 53) % 100,
        size: 2 + (i % 3),
        delay: (i % 7) * 0.8,
        duration: 9 + (i % 5) * 2,
      })),
    [count],
  );
  if (reduce) return null;
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d) => (
        <motion.span
          key={d.id}
          className="absolute rounded-full bg-brand/40"
          style={{ left: `${d.left}%`, top: `${d.top}%`, width: d.size, height: d.size, willChange: "transform, opacity" }}
          animate={{ y: [0, -26, 0], opacity: [0, 0.8, 0] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/** Abstract neural-network / waveform graphic. */
function NeuralArt() {
  const reduce = useReducedMotion();
  const nodes = [
    [40, 120], [40, 190], [40, 260],
    [150, 90], [150, 160], [150, 230], [150, 300],
    [260, 130], [260, 200], [260, 270],
    [370, 165], [370, 235],
  ] as const;
  const links: [number, number][] = [
    [0, 3], [0, 4], [1, 4], [1, 5], [2, 5], [2, 6],
    [3, 7], [4, 7], [4, 8], [5, 8], [5, 9], [6, 9],
    [7, 10], [8, 10], [8, 11], [9, 11],
  ];
  return (
    <svg viewBox="0 0 420 390" className="h-full w-full" aria-hidden="true">
      <defs>
        <linearGradient id="nl" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="hsl(var(--brand))" stopOpacity="0.15" />
          <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      {links.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]}
          stroke="url(#nl)" strokeWidth="1"
          initial={reduce ? undefined : { pathLength: 0, opacity: 0 }}
          animate={reduce ? undefined : { pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.3 + i * 0.05 }}
        />
      ))}
      {nodes.map(([x, y], i) => (
        <motion.circle
          key={i} cx={x} cy={y} r="3.5" fill="hsl(var(--brand-light))"
          initial={reduce ? undefined : { scale: 0, opacity: 0 }}
          animate={reduce ? undefined : { scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 + i * 0.06 }}
        />
      ))}
      {/* waveform */}
      <motion.path
        d="M20 350 Q 50 320, 80 350 T 140 350 T 200 350 T 260 350 T 320 350 T 400 350"
        fill="none" stroke="hsl(var(--chart-2))" strokeOpacity="0.45" strokeWidth="1.5"
        initial={reduce ? undefined : { pathLength: 0 }}
        animate={reduce ? undefined : { pathLength: 1 }}
        transition={{ duration: 1.6, delay: 0.8 }}
      />
    </svg>
  );
}

export function LoginForm() {
  const router = useRouter();
  const [step, setStep] = useState<"email" | "code" | "2fa">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [twoFaCode, setTwoFaCode] = useState("");
  // Shown inline beside the field as well as toasted. A wrong code is the most
  // common thing that happens on this screen, and a toast that has already
  // faded leaves someone staring at a form that simply refused to submit.
  const [error, setError] = useState<string | null>(null);
  // api() sends people here with ?reason=expired when their session is gone, 
  // it lapsed, they signed out everywhere, or an admin ended that device's
  // session. Saying so beats dropping them on a bare sign-in form.
  const [expired, setExpired] = useState(false);
  // Which OAuth redirect is starting: the button shows its spinner and both
  // stay disabled, so a second click cannot start a second flow.
  const [oauthPending, setOauthPending] = useState<SocialAuthProvider | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setExpired(params.get("reason") === "expired");
    // The OAuth callback redirects back here with ?2fa=pending for a
    // 2FA-enrolled account instead of setting a session cookie, pick up
    // exactly where the email-OTP path lands after its own first factor.
    // "pending" is a marker, not the challenge itself: the real token is an
    // httpOnly mfa_ticket cookie the backend already set and reads on its
    // own, so `challenge` stays null and verifyTwoFactor sends none.
    if (params.get("2fa") === "pending") {
      setStep("2fa");
    }
  }, []);
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const verifyTotpLogin = useVerifyTotpLogin();
  const { data: oauthProviders } = useOAuthProviders();
  const canGoogle = oauthProviders?.includes("google") ?? false;
  const canMicrosoft = oauthProviders?.includes("microsoft") ?? false;
  const reduce = useReducedMotion();

  // `heading` names what failed; the description is the server's message,
  // or the fallback sentence when the server gave none.
  const fail = (e: unknown, fallback: string, heading: string) => {
    const message = e instanceof Error && e.message ? e.message : fallback;
    setError(message);
    toast({ title: heading, description: message, variant: "error" });
  };

  const sendCode = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      fail(null, "Enter a valid email address.", "Email address not valid");
      return;
    }
    try {
      await requestOtp.mutateAsync(email.trim());
      setError(null);
      setCode("");
      setStep("code");
    } catch (e) {
      fail(e, "Could not send the code. Try again.", "Sign-in code not sent");
    }
  };

  const verify = async () => {
    try {
      const res = await verifyOtp.mutateAsync({ email: email.trim(), code });
      setError(null);
      if (res.needs_2fa) {
        setChallenge(res.challenge);
        setTwoFaCode("");
        setStep("2fa");
        return;
      }
      router.push(res.needs_onboarding ? "/onboarding" : "/dashboard");
    } catch (e) {
      fail(e, "That code is invalid or has expired.", "Sign-in code not accepted");
    }
  };

  const verifyTwoFactor = async () => {
    // `challenge` is only set on the email-OTP path (it came back in that
    // response body); the OAuth path leaves it null and relies entirely on
    // the mfa_ticket cookie the backend already set. Only the "2fa" step
    // ever calls this, so there's nothing else to gate on here.
    try {
      const res = await verifyTotpLogin.mutateAsync({
        ...(challenge ? { challenge } : {}),
        code: twoFaCode.trim(),
      });
      setError(null);
      router.push(res.needs_onboarding ? "/onboarding" : "/dashboard");
    } catch (e) {
      fail(e, "That code is invalid or has expired.", "Authenticator code not accepted");
    }
  };

  const resend = async () => {
    try {
      await requestOtp.mutateAsync(email.trim());
      setError(null);
      setCode("");
      toast({ title: "New code sent", description: `A new sign-in code is on its way to ${email.trim()}.`, variant: "success" });
    } catch (e) {
      // Resend is rate limited, so this genuinely fails sometimes, say so
      // instead of leaving the button looking inert.
      fail(e, "Could not resend the code. Try again shortly.", "Sign-in code not sent");
    }
  };

  const startOAuth = (provider: SocialAuthProvider) => {
    setOauthPending(provider);
    // A full page navigation, exactly as the old <a href> was.
    window.location.href = OAUTH_START[provider];
  };

  const errorNote = error ? (
    <p role="alert" aria-live="assertive" className="text-xs font-medium text-destructive">
      {error}
    </p>
  ) : null;

  const rise = reduce ? {} : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

  return (
    // The sign-in screen is always dark, whatever the app theme: scoping
    // data-theme="dark" (+ .dark) here makes every HumAIn token and `dark:`
    // variant below resolve to the dark palette. Without it the HumAIn Input
    // rendered as a white box with an invisible placeholder in light mode.
    <div
      data-theme="dark"
      className="dark min-h-screen w-full bg-background text-foreground lg:grid lg:grid-cols-[55fr_45fr]"
    >
      {/* ───────────────── Left, AI hero ───────────────── */}
      <section className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        {/* animated gradient field */}
        <div aria-hidden="true" className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_15%_10%,hsl(var(--brand)/0.38),transparent_60%),radial-gradient(90%_70%_at_85%_85%,hsl(var(--chart-2)/0.18),transparent_60%)]" />
          <motion.div
            className="absolute -left-32 top-10 h-96 w-96 rounded-full bg-brand/35 blur-3xl"
            animate={reduce ? undefined : { x: [0, 40, 0], y: [0, 30, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-chart-2/15 blur-3xl"
            animate={reduce ? undefined : { x: [0, -30, 0], y: [0, -20, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="absolute inset-0 opacity-35 [background-image:linear-gradient(hsl(var(--foreground)/0.04)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--foreground)/0.04)_1px,transparent_1px)] [background-size:3.5rem_3.5rem]" />
        </div>
        <Particles />

        <motion.div {...rise} transition={{ duration: 0.5 }} className="relative flex items-center gap-2">
          <Logo size={34} />
          <span className="text-base font-semibold tracking-tight">Voice Studio</span>
        </motion.div>

        <div className="relative grid grid-cols-[1.15fr_0.85fr] items-center gap-6">
          <motion.div {...rise} transition={{ duration: 0.6, delay: 0.1 }}>
            <h1 className="text-balance text-5xl font-semibold leading-[1.05] tracking-[-0.02em]">
              Build Human-like
              <br />
              <span className="bg-gradient-to-r from-brand via-brand-deep to-chart-2 bg-clip-text text-transparent">
                Voice Agents
              </span>
            </h1>
            <p className="mt-5 max-w-[46ch] text-base leading-relaxed text-muted-foreground">
              Create multilingual enterprise AI voice agents powered by the world&apos;s best LLMs.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {CHIPS.map((c, i) => (
                <motion.span
                  key={c}
                  initial={reduce ? undefined : { opacity: 0, y: 8 }}
                  animate={reduce ? undefined : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.25 + i * 0.05 }}
                  className="rounded-full border border-border bg-foreground/5 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm transition hover:border-brand/40 hover:bg-foreground/10 hover:text-foreground"
                >
                  {c}
                </motion.span>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={reduce ? undefined : { opacity: 0, scale: 0.96 }}
            animate={reduce ? undefined : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="hidden h-80 xl:block"
          >
            <NeuralArt />
          </motion.div>
        </div>

        <motion.div {...rise} transition={{ duration: 0.5, delay: 0.5 }} className="relative">
          <p className="text-xs font-semibold uppercase tracking-caps text-muted-foreground">
            Trusted by enterprise teams
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-3">
            {TRUST.map((t) => (
              <span key={t} className="text-sm font-semibold tracking-tight text-muted-foreground/60 transition hover:text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ───────────────── Right, auth card ───────────────── */}
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden p-5 lg:min-h-0 lg:bg-surface">
        <div aria-hidden="true" className="absolute inset-0 lg:hidden">
          <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,hsl(var(--brand)/0.35),transparent_60%)]" />
        </div>

        <motion.div
          initial={reduce ? undefined : { opacity: 0, y: 18 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-md"
        >
          <div className="rounded-card border border-border bg-surface/80 p-7 backdrop-blur-2xl sm:p-8">
            <div className="mb-6 flex items-center gap-2 lg:hidden">
              <Logo size={30} />
              <span className="text-sm font-semibold tracking-tight">Voice Studio</span>
            </div>

            <h2 className="text-2xl font-semibold tracking-[-0.01em]">Welcome Back</h2>
            <p className="mt-2 text-sm text-muted-foreground">Continue to your workspace</p>

            {expired && (
              <Alert variant="warning" className="mt-4">
                <TriangleAlert className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>Your session has expired. Please sign in again.</AlertDescription>
              </Alert>
            )}

            {step === "2fa" ? (
              <form
                className="mt-7 space-y-4"
                onSubmit={(e) => { e.preventDefault(); verifyTwoFactor(); }}
              >
                {/* Free text, not InputOTP: a backup code is accepted here too. */}
                <div className="space-y-2">
                  <Input
                    id="twofa"
                    label="Two-factor code"
                    pinLabel
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={twoFaCode}
                    onChange={(e) => { setTwoFaCode(e.target.value); setError(null); }}
                    aria-invalid={!!error}
                    placeholder="123456"
                  />
                  {errorNote ?? (
                    <p className="text-2xs text-muted-foreground">
                      From your authenticator app, or one of your backup codes.
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full" pending={verifyTotpLogin.isPending}>
                  Verify &amp; continue
                </Button>
                <Button
                  type="button"
                  variant="link-neutral"
                  onClick={() => { setError(null); setChallenge(null); setStep("email"); }}
                >
                  ← Start over
                </Button>
              </form>
            ) : step === "email" ? (
              <form
                className="mt-7 space-y-4"
                onSubmit={(e) => { e.preventDefault(); sendCode(); }}
              >
                <div className="space-y-2">
                  <Input
                    id="email"
                    type="email"
                    label="Work email"
                    pinLabel
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    aria-invalid={!!error}
                    placeholder="you@company.com"
                  />
                  {errorNote}
                </div>
                <Button type="submit" className="w-full" pending={requestOtp.isPending}>
                  Continue
                  {!requestOtp.isPending && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                </Button>
                <p className="text-center text-2xs leading-relaxed text-muted-foreground">
                  We&apos;ll send a secure magic link. No password required.
                </p>
              </form>
            ) : (
              <form
                className="mt-7 space-y-4"
                onSubmit={(e) => { e.preventDefault(); verify(); }}
              >
                <div className="space-y-2">
                  <Label id="code-label" className="text-xs text-muted-foreground">Verification code</Label>
                  {/* Submitted by the Verify button, as before, not on the
                      sixth digit, which would change when the request fires. */}
                  <InputOTP
                    id="code"
                    aria-labelledby="code-label"
                    aria-describedby="code-help"
                    autoFocus
                    value={code}
                    onChange={(v) => { setCode(v.replace(/\D/g, "").slice(0, 6)); setError(null); }}
                    disabled={verifyOtp.isPending}
                    className="justify-center"
                  />
                  <div id="code-help">
                    {errorNote ?? <p className="text-2xs text-muted-foreground">Sent to {email}</p>}
                  </div>
                </div>
                <Button type="submit" className="w-full" pending={verifyOtp.isPending}>
                  Verify &amp; continue
                </Button>
                <div className="flex items-center justify-between">
                  <Button type="button" variant="link-neutral" onClick={() => { setError(null); setStep("email"); }}>
                    ← Different email
                  </Button>
                  <Button type="button" variant="link" pending={requestOtp.isPending} onClick={resend}>
                    Resend code
                  </Button>
                </div>
              </form>
            )}

            {step !== "2fa" && (canGoogle || canMicrosoft) && (
              <>
                <Separator label="or" className="my-6" />

                <div className="space-y-3">
                  {canGoogle && (
                    <SocialAuthButton
                      provider="google"
                      pending={oauthPending === "google"}
                      disabled={oauthPending !== null}
                      onClick={() => startOAuth("google")}
                    />
                  )}
                  {canMicrosoft && (
                    <SocialAuthButton
                      provider="microsoft"
                      pending={oauthPending === "microsoft"}
                      disabled={oauthPending !== null}
                      onClick={() => startOAuth("microsoft")}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {BADGES.map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-2xs font-medium text-muted-foreground">
                <Icon className="h-4 w-4 text-brand" aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>
          <p className="mt-6 text-center text-2xs text-muted-foreground/70">
            &copy; {new Date().getFullYear()} TensorGo Software Private Limited. All Rights Reserved.
          </p>
        </motion.div>
      </section>
    </div>
  );
}
